import React, { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { ReservationLimitInPeriodContext } from '.';
import { useVenueListReservationsCountQuery } from '../../hooks/reactQuery/reservations/useVenueListReservationsCountQuery';
import { useAppSelector } from '../../redux/hooks';

const getApprovalBasedPeriod = (approvalDate: string): { startDate: string; endDate: string } => {
  const approvalUtc = dayjs.utc(approvalDate).startOf('day');
  const monthsElapsed = dayjs.utc().diff(approvalUtc, 'month');

  const periodStart = approvalUtc.add(monthsElapsed, 'month');
  const periodEnd = periodStart.add(1, 'month');

  return {
    startDate: periodStart.toISOString(),
    endDate: periodEnd.toISOString(),
  };
};

export const ReservationsLimitInPeriodProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription, isLoading } = useAppSelector((state) => state.subscription.value);

  const { startDate, endDate } = useMemo(() => {
    if (!isLoading && subscription) {
      // Active subscription: use subscription dates directly
      if (subscription.status === 'active') {
        return {
          startDate: subscription.updatedAt,
          endDate: subscription.expirationDate,
        };
      }

      // Expired subscription: use expirationDate as base for rolling monthly period
      if (subscription.expirationDate) {
        return getApprovalBasedPeriod(subscription.expirationDate);
      }
    }

    // No subscription (free plan): use approvalDate
    if (venueState?.approvalDate) {
      return getApprovalBasedPeriod(venueState.approvalDate);
    }

    return { startDate: '', endDate: '' };
  }, [isLoading, subscription, venueState?.approvalDate]);

  const { data: reservationsCount } = useVenueListReservationsCountQuery(
    {
      venueID: venueState?.id || '',
      subscriptionDates: {
        start: startDate || '',
        end: endDate || '',
      },
      privateOffer: false,
    },
    !!startDate && !!endDate && !!venueState,
  );

  const getReservationsLimitInPeriodHandle = async () => {
    await queryClient.invalidateQueries({ queryKey: ['useVenueListReservationsCountAPI'] });
  };

  return (
    <ReservationLimitInPeriodContext.Provider
      value={{
        totalWaitingAndShippedReservations:
          (reservationsCount?.totalShippedWithoutInvite || 0) +
          (reservationsCount?.totalWatingShippingWithoutInvite || 0),
        totalReservedAndCheckedInReservations:
          (reservationsCount?.totalReservedWithoutInvite || 0) +
          (reservationsCount?.totalCheckedInWithoutInvite || 0),
        totalCompletedPromotions: (reservationsCount?.totalCompletedPromotions || 0) + (Number(reservationsCount?.totalShippedWithoutInvite) || 0) + (Number(reservationsCount?.totalWatingShippingWithoutInvite) || 0),
        periodStartDate: startDate || '',
        periodEndDate: endDate || '',
        getReservationsLimitInPeriodHandle,
      }}
    >
      {children}
    </ReservationLimitInPeriodContext.Provider>
  );
};
